/**
 * Tracker → ingest beacon contract.
 *
 * The tracker (packages/tracker) produces JSON matching these shapes (it imports
 * only the TYPES, so nothing here is bundled into the ~8KB script). The ingest
 * service validates incoming beacons with these Zod schemas (Palier 1C).
 *
 * Light signature: `sig` is an HMAC-SHA256 (hex) of `beaconSignatureMessage()`
 * using the project's secret key. Combined with the timestamp window + a one-time
 * nonce (server-side, Redis), it stops trivial tampering and replay — NOT forged
 * fraud (a client-side secret is extractable). Real fraud scoring is Palier 2.
 */

import { z } from 'zod';

export const BEACON_VERSION = 1;

/** Max age (ms) of a beacon timestamp the ingest will accept (anti-replay window). */
export const BEACON_MAX_AGE_MS = 5 * 60 * 1000;

export const beaconEventTypeSchema = z.enum([
  'click',
  'scroll',
  'form_start',
  'form_submit',
  'phone',
  'whatsapp',
  'outbound',
  'custom',
  'sensitive_path',
  'honeypot',
  // Palier B — conversion tracking.
  'conversion',
  'download',
]);
export type BeaconEventType = z.infer<typeof beaconEventTypeSchema>;

const pageviewDataSchema = z.object({
  url: z.string().max(2048),
  path: z.string().max(2048),
  title: z.string().max(512),
  referrer: z.string().max(2048),
  ms: z.number().int().nonnegative(),
});

const eventDataSchema = z.object({
  type: beaconEventTypeSchema,
  name: z.string().max(128),
  data: z.string().max(2048),
  url: z.string().max(2048),
  ms: z.number().int().nonnegative(),
});

const sessionDataSchema = z.object({
  landingPage: z.string().max(2048),
  exitPage: z.string().max(2048),
  sessionMs: z.number().int().nonnegative(),
  pageviews: z.number().int().nonnegative(),
  events: z.number().int().nonnegative(),
  scrollMaxPct: z.number().int().min(0).max(100),
  hasInteraction: z.boolean(),
  formStarted: z.boolean(),
  formSubmitted: z.boolean(),
  phoneClicks: z.number().int().nonnegative(),
  whatsappClicks: z.number().int().nonnegative(),
  honeypotHit: z.boolean(),
  viewport: z.string().max(32),
  resolution: z.string().max(32),
  pixelRatio: z.number(),
  language: z.string().max(64),
  tzOffset: z.number().int(),
  tzLabel: z.string().max(64),
  referrer: z.string().max(2048),
  utmSource: z.string().max(255),
  utmMedium: z.string().max(255),
  utmCampaign: z.string().max(255),
  utmTerm: z.string().max(255),
  utmContent: z.string().max(255),
  gclid: z.string().max(255),
  gbraid: z.string().max(255),
  wbraid: z.string().max(255),
  fpCanvas: z.string().max(64),
  fpWebgl: z.string().max(64),
  fpAudio: z.string().max(64),
  fpCombined: z.string().max(64),
  dnt: z.boolean(),

  // --- Extended fingerprint (Palier 2A, SCORING-SPEC §6) -------------------
  // Always present. Sentinels mark "unavailable/unknown": '' for strings,
  // -1 for numerics that can't be read (vs a real 0). `.default()` keeps older
  // P1 beacons (which omit these) valid during the tracker rollout.
  platform: z.string().max(128).default(''),
  hardwareConcurrency: z.number().int().default(-1),
  deviceMemory: z.number().default(-1),
  touchSupport: z.boolean().default(false),
  maxTouchPoints: z.number().int().default(-1),
  cookiesEnabled: z.boolean().default(false),
  localStorageAvailable: z.boolean().default(false),
  sessionStorageAvailable: z.boolean().default(false),
  webdriver: z.boolean().default(false),
  pluginsCount: z.number().int().default(-1),
  mimeTypesCount: z.number().int().default(-1),
  webglVendor: z.string().max(64).default(''),
  webglRenderer: z.string().max(64).default(''),
  fontsHash: z.string().max(64).default(''),
  permissionsState: z.string().max(64).default(''),
  visibilityEvents: z.number().int().default(0),
  focusEvents: z.number().int().default(0),
  blurEvents: z.number().int().default(0),
  firstInteractionDelay: z.number().int().default(-1),
  mouseEvents: z.number().int().default(0),
  touchEvents: z.number().int().default(0),
  keyboardEvents: z.number().int().default(0),
  clickEvents: z.number().int().default(0),
  formEvents: z.number().int().default(0),
  pageLoadTime: z.number().int().default(-1),
  timeToFirstEvent: z.number().int().default(-1),
  interactionCount: z.number().int().default(0),
  fingerprintConfidence: z.number().int().default(-1),
});

const envelope = {
  v: z.literal(BEACON_VERSION),
  k: z.string().regex(/^[0-9a-f]{32}$/),
  vid: z.string().uuid(),
  cid: z.string().uuid().nullable(),
  ts: z.number().int().positive(),
  n: z.string().regex(/^[0-9a-f]{8,32}$/),
  sig: z.string().max(128),
};

export const beaconSchema = z.discriminatedUnion('t', [
  z.object({ ...envelope, t: z.literal('pageview'), d: pageviewDataSchema }),
  z.object({ ...envelope, t: z.literal('event'), d: eventDataSchema }),
  z.object({ ...envelope, t: z.literal('session'), d: sessionDataSchema }),
]);

export type Beacon = z.infer<typeof beaconSchema>;
export type PageviewBeacon = Extract<Beacon, { t: 'pageview' }>;
export type EventBeacon = Extract<Beacon, { t: 'event' }>;
export type SessionBeacon = Extract<Beacon, { t: 'session' }>;
export type BeaconType = Beacon['t'];

/**
 * Canonical message that is HMAC-signed. Both the tracker and the ingest MUST
 * build it identically.
 */
export function beaconSignatureMessage(b: {
  k: string;
  t: string;
  vid: string;
  ts: number;
  n: string;
}): string {
  return `${b.k}.${b.t}.${b.vid}.${b.ts}.${b.n}`;
}
